import { useCallback, useMemo, useState } from "react";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, EmptyState, Select } from "@/components/ui";
import { useApiClient } from "@/api/useApiClient";
import {
  AttentionList,
  BarList,
  DayColumns,
  KpiCard,
  KpiGrid,
  LoadBoundary,
  Panel,
  TwoCol,
  useHubLoad,
  useHubNavigate,
  type AttentionEntry,
} from "@/modules/hub-kit/HubKit";
import { ageLabel, formatCount, formatDay, formatMinor, humanize } from "@/modules/hub-kit/format";
import {
  createFinanceOverviewApi,
  type FinanceOverview,
  type IntegrityCheck,
} from "../overviewApi";

const PERIODS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

function windowFor(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

const PAYOUT_TONE: Record<string, "warning" | "danger" | "success" | undefined> = {
  held: "warning",
  delayed: "warning",
  pending_review: "warning",
  failed: "danger",
  disputed: "danger",
  paid: "success",
  settled: "success",
  reconciled: "success",
};

export function FinanceOverviewTab() {
  const client = useApiClient();
  const api = useMemo(() => createFinanceOverviewApi(client), [client]);
  const go = useHubNavigate();
  const [days, setDays] = useState("30");

  const loadOverview = useCallback(async () => {
    const w = windowFor(Number(days));
    return (await api.getOverview(w.from, w.to)).overview;
  }, [api, days]);
  const overview = useHubLoad(loadOverview);

  const loadChecks = useCallback(async () => api.getIntegrityChecks(), [api]);
  const checks = useHubLoad(loadChecks);

  const reloadAll = () => {
    void overview.reload();
    void checks.reload();
  };

  const o = overview.data;

  return (
    <Stack gap={24}>
      <Flex gap={12} align="end" wrap>
        <Select
          label="Period"
          name="finance-period"
          value={days}
          onChange={(e) => setDays(e.target.value)}
          options={PERIODS}
        />
        <Button variant="secondary" onClick={reloadAll}>
          Refresh
        </Button>
        {o ? (
          <Text role="caption" color="muted" as="span">
            Payments counted by the day they were made (Manila time). Refunds by the day requested. Queues are as of now.
          </Text>
        ) : null}
      </Flex>
      <LoadBoundary
        loading={overview.loading}
        error={overview.error}
        onRetry={() => void overview.reload()}
        hasData={!!o}
        what="the finance overview"
      >
        {o ? (
          <OverviewBody
            o={o}
            checks={checks.data?.checks ?? null}
            checksError={checks.error}
            go={go}
          />
        ) : null}
      </LoadBoundary>
    </Stack>
  );
}

function OverviewBody({
  o,
  checks,
  checksError,
  go,
}: {
  o: FinanceOverview;
  checks: IntegrityCheck[] | null;
  checksError: string | null;
  go: (tab: string, sub?: string) => void;
}) {
  const cur = o.primary_currency;
  const k = o.kpis[cur]!;
  const empty =
    k.captured_count === 0 &&
    k.pending_payment_count === 0 &&
    k.failed_payment_count === 0 &&
    k.payouts_owed_count === 0 &&
    k.payouts_sent_minor === 0 &&
    o.refunds_by_status.length === 0;

  const attention: AttentionEntry[] = [];
  for (const c of checks ?? []) {
    if (c.count === 0) continue;
    attention.push({
      key: `check-${c.code}`,
      severity: c.severity === "critical" ? "critical" : c.severity === "high" ? "warning" : "info",
      title: `${c.title} (${c.count})`,
      detail: c.description,
      actionLabel: "Review",
      onAction: () => go("reconciliation"),
    });
  }
  if (o.queues.exceptions_open_critical > 0) {
    attention.push({
      key: "exc-critical",
      severity: "critical",
      title: `${o.queues.exceptions_open_critical} critical or urgent exception${o.queues.exceptions_open_critical === 1 ? "" : "s"} open`,
      actionLabel: "Open exceptions",
      onAction: () => go("reconciliation", "exceptions"),
    });
  }
  if (o.queues.refunds_open > 0) {
    const oldest = o.refunds_by_status
      .filter((r) => ["requested", "under_review", "approved", "requires_intervention", "failed"].includes(r.status))
      .map((r) => r.oldest_requested_at)
      .filter((x): x is string => !!x)
      .sort()[0];
    attention.push({
      key: "refunds-open",
      severity: "warning",
      title: `${o.queues.refunds_open} refund${o.queues.refunds_open === 1 ? "" : "s"} waiting`,
      detail: `${formatMinor(k.refunds_open_minor, cur)} in total${oldest ? `, oldest waiting ${ageLabel(oldest)}` : ""}`,
      actionLabel: "Open refunds",
      onAction: () => go("refunds", "refunds"),
    });
  }
  if (o.queues.payouts_to_action > 0) {
    attention.push({
      key: "payouts-action",
      severity: "warning",
      title: `${o.queues.payouts_to_action} payout${o.queues.payouts_to_action === 1 ? "" : "s"} need action`,
      detail: "Eligible, in review, held, delayed or failed.",
      actionLabel: "Open payouts",
      onAction: () => go("payouts", "payouts"),
    });
  }
  if (o.queues.disputes_open > 0) {
    attention.push({
      key: "disputes-open",
      severity: "warning",
      title: `${o.queues.disputes_open} open dispute${o.queues.disputes_open === 1 ? "" : "s"}`,
      actionLabel: "Open disputes",
      onAction: () => go("refunds", "disputes"),
    });
  }
  if (o.queues.settlements_unmatched > 0) {
    attention.push({
      key: "settle-unmatched",
      severity: "warning",
      title: `${o.queues.settlements_unmatched} unmatched or failed settlement${o.queues.settlements_unmatched === 1 ? "" : "s"}`,
      actionLabel: "Open settlements",
      onAction: () => go("payouts", "settlements"),
    });
  }
  if (k.captured_count > 0 && o.fee.unset_count > 0) {
    attention.push({
      key: "fee-unset",
      severity: "info",
      title: `${o.fee.unset_count} captured payment${o.fee.unset_count === 1 ? " was" : "s were"} recorded without a platform fee`,
      detail: "The platform fee rule is not configured, so platform revenue reads zero.",
      actionLabel: "See fee status",
      onAction: () => go("fees"),
    });
  }

  const dayRows = o.daily
    .filter((d) => d.currency === cur)
    .map((d) => ({
      day: formatDay(d.day),
      value: d.amount_minor,
      title: `${formatDay(d.day)}: ${formatMinor(d.amount_minor, cur)} from ${formatCount(d.count)} payment${d.count === 1 ? "" : "s"}`,
    }));

  const payoutRows = o.payouts_by_status
    .filter((p) => p.currency === cur)
    .map((p) => ({
      key: p.status,
      label: `${humanize(p.status)} (${formatCount(p.count)})`,
      value: p.amount_minor,
      display: formatMinor(p.amount_minor, cur),
      tone: PAYOUT_TONE[p.status],
    }));

  const refundRows = o.refunds_by_status
    .filter((r) => r.currency === cur)
    .map((r) => ({
      key: r.status,
      label: `${humanize(r.status)} (${formatCount(r.count)})`,
      value: r.amount_minor,
      display: formatMinor(r.amount_minor, cur),
      tone: (["failed", "requires_intervention"].includes(r.status)
        ? "danger"
        : ["executed", "settled"].includes(r.status)
          ? "success"
          : ["requested", "under_review", "approved"].includes(r.status)
            ? "warning"
            : undefined) as "warning" | "danger" | "success" | undefined,
    }));

  const settlementRows = o.settlements_by_status.map((s) => ({
    key: s.status,
    label: `${humanize(s.status)} (${formatCount(s.count)})`,
    value: s.expected_minor,
    display: `${formatMinor(s.settled_minor, cur)} of ${formatMinor(s.expected_minor, cur)}`,
    tone: (s.status === "unmatched" || s.status === "failed"
      ? "danger"
      : s.status === "reconciled" || s.status === "settled"
        ? "success"
        : "warning") as "warning" | "danger" | "success",
  }));

  return (
    <Stack gap={24}>
      {o.currencies.length > 1 ? (
        <Alert tone="warning" title="More than one currency in the ledger">
          Totals below are for {cur} only. Other currencies present: {o.currencies.filter((c) => c !== cur).join(", ")}. They are never added together.
        </Alert>
      ) : null}

      {empty ? (
        <EmptyState
          title="No money has moved yet"
          description="Payments appear here once a player pays for a held court through Xendit checkout and the payment webhook confirms it. Refunds and payouts appear as they are requested and created."
          action={
            <Button variant="secondary" onClick={() => go("payments")}>
              Open Payments
            </Button>
          }
        />
      ) : null}

      <KpiGrid>
        <KpiCard
          label="Gross bookings"
          value={formatMinor(k.gmv_minor, cur)}
          hint={`${formatCount(k.captured_count)} captured payment${k.captured_count === 1 ? "" : "s"}`}
          onClick={() => go("payments")}
        />
        <KpiCard
          label="Platform revenue"
          value={formatMinor(k.platform_revenue_minor, cur)}
          hint={
            o.fee.unset_count > 0 && k.platform_revenue_minor === 0
              ? "Fee rule not configured"
              : "Fees and commission on those payments"
          }
          onClick={() => go("fees")}
        />
        <KpiCard
          label="Refunded"
          value={formatMinor(k.refunds_paid_minor, cur)}
          hint={`${formatMinor(k.refunds_requested_minor, cur)} requested in the period`}
          onClick={() => go("refunds", "refunds")}
        />
        <KpiCard
          label="Owed to businesses"
          value={formatMinor(k.payouts_owed_minor, cur)}
          hint={`${formatCount(k.payouts_owed_count)} payout${k.payouts_owed_count === 1 ? "" : "s"} open${k.payouts_held_count ? `, ${k.payouts_held_count} held` : ""}`}
          onClick={() => go("payouts", "payouts")}
        />
        <KpiCard
          label="Paid out (all time)"
          value={formatMinor(k.payouts_sent_minor, cur)}
          hint="Paid, settled or reconciled payouts"
        />
        <KpiCard
          label="Awaiting payment"
          value={formatMinor(k.pending_payment_minor, cur)}
          hint={`${formatCount(k.pending_payment_count)} started, not confirmed${k.failed_payment_count ? ` · ${k.failed_payment_count} failed` : ""}`}
          {...(k.failed_payment_count > 0 ? { tone: "warning" as const } : {})}
          onClick={() => go("payments")}
        />
      </KpiGrid>

      <Panel
        title="Needs attention"
        description={
          checksError
            ? `The ledger checks could not run: ${checksError}`
            : "Ledger integrity checks and open queues. Nothing here is estimated."
        }
      >
        <AttentionList
          entries={attention}
          clearMessage={
            checks
              ? `All clear: ${checks.length} ledger checks ran and found nothing, and no queue has waiting work.`
              : "Queues are clear."
          }
        />
      </Panel>

      <TwoCol>
        <Panel title="Gross bookings by day" description="Captured booking payments, Manila calendar days.">
          <DayColumns days={dayRows} emptyMessage="No captured payments in this period." />
        </Panel>
        <Panel
          title="Payouts to businesses"
          description="Amount by status, all time."
          actions={
            <Button variant="ghost" size="sm" onClick={() => go("payouts", "payouts")}>
              Open
            </Button>
          }
        >
          <BarList rows={payoutRows} emptyMessage="No payouts created yet." />
        </Panel>
        <Panel
          title="Refunds"
          description="Amount by status, all time."
          actions={
            <Button variant="ghost" size="sm" onClick={() => go("refunds", "refunds")}>
              Open
            </Button>
          }
        >
          <BarList rows={refundRows} emptyMessage="No refunds requested yet." />
        </Panel>
        <Panel
          title="Settlements"
          description="Settled amount against what was expected."
          actions={
            <Button variant="ghost" size="sm" onClick={() => go("payouts", "settlements")}>
              Open
            </Button>
          }
        >
          <BarList rows={settlementRows} emptyMessage="No settlements recorded yet." />
        </Panel>
      </TwoCol>
    </Stack>
  );
}
